import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, RotateCcw } from 'lucide-react';
import { Button } from '../ui/button';
import { getApiHeaders } from '../../utils/apiHelpers';
import { API_ARCHIVIO, dataItaliana, mb } from './archivioApi';

const PAGINA = 20;

const dettagli = (v) => [
  v.numero && `n. ${v.numero}`,
  v.ddt_esiste ? dataItaliana(v.data_ddt) : `tolta il ${dataItaliana(v.eliminata_at?.slice(0, 10))}`,
  mb(v.byte),
  v.motivo === 'nuovo_ddt' && 'tolta dopo la scansione',
].filter(Boolean).join(' · ');

const Scadenza = ({ v }) => (
  <p className={`text-xs ${v.giorni === 0 ? 'text-amber-700 font-medium' : 'text-gray-500'}`} data-testid={`cestino-giorni-${v.ddt_id}`}>
    {v.giorni === 0 ? 'Oltre 30 giorni: si può eliminare' : `Si potrà eliminare tra ${v.giorni} ${v.giorni === 1 ? 'giorno' : 'giorni'}`}
  </p>
);

const VoceCestino = ({ v, onRipristina, occupato }) => (
  <li className="flex items-center justify-between gap-3 py-2.5" data-testid={`cestino-voce-${v.ddt_id}`}>
    <div className="min-w-0">
      <p className="text-sm font-medium text-gray-900 truncate">{v.ddt_esiste ? (v.fornitore || 'Fornitore non indicato') : 'DDT cancellato'}</p>
      <p className="text-xs text-gray-500 truncate">{dettagli(v)}</p>
      <Scadenza v={v} />
    </div>
    <Button size="sm" variant="outline" className="shrink-0" onClick={() => onRipristina(v.ddt_id)} disabled={!!occupato}
      data-testid={`cestino-ripristina-${v.ddt_id}`}>
      {occupato === v.ddt_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
      <span className="ml-1">Ripristina</span>
    </Button>
  </li>
);

export const ElencoCestino = ({ n, onCambiato }) => {
  const [voci, setVoci] = useState(null);
  const [totale, setTotale] = useState(0);
  const [occupato, setOccupato] = useState(null);
  const caricate = useRef(PAGINA);

  const carica = useCallback(async (da = 0, quante = PAGINA) => {
    try {
      const { data } = await axios.get(`${API_ARCHIVIO}/spazio/cestino`, { headers: getApiHeaders(), params: { da, n: quante } });
      setVoci((prima) => {
        const tutte = da ? [...(prima || []), ...data.voci] : data.voci;
        caricate.current = Math.max(PAGINA, tutte.length);
        return tutte;
      });
      setTotale(data.totale);
    } catch (e) {
      toast.error('Elenco del cestino non disponibile');
      setVoci((prima) => prima || []);
    }
  }, []);

  useEffect(() => { carica(0, Math.min(100, caricate.current)); }, [carica, n]);

  const ripristinaUna = async (ddtId) => {
    setOccupato(ddtId);
    try {
      await axios.post(`${API_ARCHIVIO}/spazio/cestino/${ddtId}/ripristina`, {}, { headers: getApiHeaders(), timeout: 60000 });
      toast.success('Foto ripristinata');
      setVoci((prima) => prima.filter((v) => v.ddt_id !== ddtId));
      onCambiato();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Ripristino non riuscito, riprova');
    } finally {
      setOccupato(null);
    }
  };

  if (!voci) return <p className="flex items-center gap-2 text-xs text-gray-500 py-2" data-testid="cestino-caricamento"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Carico il cestino…</p>;
  return (
    <div className="min-w-0" data-testid="cestino-elenco">
      <ul className="divide-y divide-gray-100 max-h-72 overflow-y-auto pr-1">
        {voci.map((v) => <VoceCestino key={v.ddt_id} v={v} onRipristina={ripristinaUna} occupato={occupato} />)}
      </ul>
      {voci.length < totale && (
        <Button variant="ghost" size="sm" className="w-full mt-1 text-purple-700" onClick={() => carica(voci.length)} data-testid="cestino-altre">
          Mostra altre ({totale - voci.length})
        </Button>
      )}
    </div>
  );
};
